import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "react-hot-toast";
import { User, BlogApi } from "../../../service";
import APPLICATION_SETTINGS from "../../../settings";
import { UserContext } from "../../context/UserContext";
import { faqs } from "../../constant/Home/MainPage";
import BackgroundImage from "assets/Home/Background.png";
import LicenseInsuranceIcon from "assets/Home/LicenseInsurance.svg";
import LocationIcon from "assets/Home/Location.svg";
import StarIcon from "assets/Home/Star.svg";
import CheckCircleIcon from "assets/Home/CheckCircle.svg";
import PlusIcon from "assets/Home/Plus.svg";
import MinusIcon from "assets/Home/Minus.svg";
import StarReviewsIcon from "assets/Home/StarReviews.svg";
import BuildingIcon from "assets/Home/Building.svg";
import HomeOwnersIcon from "assets/Home/HomeOwnersBuilding.svg";
import BriefcaseIcon from "assets/Home/Briefcase.svg";
import PropertyGroundIcon from "assets/icons/PropertyGround.svg";
import PlumbingIcon from "assets/icons/Plumbing.svg";
import ElectricalIcon from "assets/icons/Electrical.svg";
import SnowFlakeIcon from "assets/icons/SnowFlake.svg";
import AppliancesIcon from "assets/icons/Appliances.svg";
import DoorsWindowsIcon from "assets/icons/DoorsWindows.svg";
import LockedOutIcon from "assets/icons/LockedOut.svg";
import GeneralMaintenanceIcon from "assets/icons/GeneralMaintainance.svg";
import PestControlIcon from "assets/icons/PestControl.svg";
import ExteriorIcon from "assets/icons/Exterior.svg";
import StructuralIcon from "assets/icons/Structural.svg";
import RoofingIcon from "assets/icons/Roofing.svg";
import OthersIcon from "assets/icons/Others.svg";
import PatternImage from "assets/Home/Container.png";
import LogoImage from "assets/Logo.png";
import VendorApplyCTA from "./VendorApplyCTA";
import CabinIcon from "@mui/icons-material/Cabin";
import "./Home.css";

const trustCards = [
  {
    icon: LicenseInsuranceIcon,
    title: "Licensed &\nInsured",
    description:
      "We verify every professional's license and insurance before they join.",
  },
  {
    icon: LocationIcon,
    title: "Locally\nVetted",
    description:
      "All vendors are established local tradesmen, with proven track records of reliability and quality work.",
  },
  {
    icon: StarIcon,
    title: "Highly\nRated",
    description:
      "Vendors are selected based on client references, reviews, and consistent performance.",
  },
];

const workflowCards = [
  {
    number: "01",
    title: "Submit a Request",
    subtitle: "Tell us what's wrong in minutes.",
    description:
      "Choose the service you need, select your availability, and upload photos if helpful.",
  },
  {
    number: "02",
    title: "A Pro Is On The Way",
    subtitle: "A licensed, vetted pro is sent to you.",
    description:
      "We match and dispatch an approved vendor based on your issue, location, and availability.",
  },
  {
    number: "03",
    title: "Full Visibility",
    subtitle: "Real-time updates at your fingertips.",
    description:
      "Track progress, communicate if needed, and pay only for completed work once the job is done.",
  },
];

const serviceCards = [
  { icon: PropertyGroundIcon, title: "Property grounds" },
  { icon: PlumbingIcon, title: "Plumbing" },
  { icon: ElectricalIcon, title: "Electrical" },
  { icon: SnowFlakeIcon, title: "HVAC" },
  { icon: AppliancesIcon, title: "Appliances" },
  { icon: DoorsWindowsIcon, title: "Doors & Windows" },
  { icon: LockedOutIcon, title: "Locked Out" },
  { icon: GeneralMaintenanceIcon, title: "General Maintenance" },
  { icon: PestControlIcon, title: "Pest Control" },
  { icon: ExteriorIcon, title: "Exterior" },
  { icon: StructuralIcon, title: "Structural" },
  { icon: RoofingIcon, title: "Roofing" },
  { icon: OthersIcon, title: "Other" },
];

const ownerCards = [
  {
    icon: BuildingIcon,
    title: "For Landlords",
    description:
      "Everything in one place to save you time and money on maintenance.",
    buttonText: "I am a Landlord",
    route: "/landlords",
    items: [
      "Maintenance coverage without the manager markup",
      "Simplified tenant coordination and maintenance tracking",
      "A vendor network you don't have to build yourself",
    ],
  },
  {
    icon: HomeOwnersIcon,
    title: "For Homeowners",
    description:
      "Professionals you can trust to enter your home and do the job right.",
    buttonText: "I am a Homeowner",
    route: "/homeowners",
    items: [
      "Professionals that handle everything from start to finish",
      "Peace of mind knowing the work will be done right",
      "Avoid the headache of searching and vetting contractors",
    ],
  },
  {
    icon: CabinIcon,
    isMuiIcon: true,
    title: "For Short Term\nRental Hosts",
    description: "5 star ratings require 5 star maintenance - Fast.",
    buttonText: "I am a Host",
    route: "/short-term-rentals",
    items: [
      "Fast, reliable service for urgent guest-related maintenance",
      "Consistent quality across all rentals",
      "Hassle-free scheduling and communication with vetted professionals",
    ],
  },
  {
    icon: BriefcaseIcon,
    title: "For Property\nManagers",
    description: "Manage multiple properties without chasing contractors",
    buttonText: "I am a Manager",
    route: "/property-managers",
    items: [
      "A network of vendors who get the job done right the first time",
      "Easily manage maintenance across all properties",
      "Save time and money with centralized job tracking and vendor coordination",
    ],
  },
];

const testimonials = [
  {
    title: "Outstanding choice!",
    description:
      "Properteasy has revolutionized how we handle maintenance across our 50+ properties. The quality of contractors and speed of service is exceptional.",
  },
  {
    title: "Strongly endorsed!",
    description:
      "Since joining Properteasy, my business has grown 40%. The jobs are quality and the payment system gives me confidence.",
  },
  {
    title: "Exceptional suggestion!",
    description:
      "As a first-time landlord, Properteasy made property maintenance stress-free. I found a reliable handyman who now handles all my properties.",
  },
];

const Home = () => {
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const userData = userContext?.userData || null;

  const [blogs, setBlogs] = useState([]);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [hoveredOwnerCardIndex, setHoveredOwnerCardIndex] = useState(null);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await BlogApi.getPosts(1);
        const data = response?.data || response || [];
        const posts = data.results || data || [];
        setBlogs(posts.slice(0, 3));
      } catch (error) {
        console.error("Failed to fetch blog posts:", error);
      }
    };
    fetchBlogs();
  }, []);

  const navigateAndScroll = (route) => {
    navigate(route);
    window.scrollTo(0, 0);
  };

  const handlePrimaryCta = () => {
    if (userData) {
      navigateAndScroll("/dashboard");
      return;
    }
    navigateAndScroll("/signup");
  };

  const handleMaintenanceRequest = () => {
    if (userData) {
      navigateAndScroll("/post-job");
      return;
    }
    navigateAndScroll("/login");
  };

  const handleNewsletterSubmit = async (event) => {
    event.preventDefault();
    if (newsletterLoading) return;

    const trimmedEmail = newsletterEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      setNewsletterLoading(true);
      await User.subscribeNewsletter(trimmedEmail);
      toast.success("You're subscribed to the newsletter!");
      setNewsletterEmail("");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Subscription failed. Please try again.";
      toast.error(message);
    } finally {
      setNewsletterLoading(false);
    }
  };

  const normalizedFaqAnswer = (value) => value.replace(/\s+/g, " ").trim();

  const buildImageUrl = (path) => {
    if (!path) return null;
    try {
      return new URL(path, APPLICATION_SETTINGS.VITE_API_BASE).toString();
    } catch (error) {
      return path;
    }
  };

  return (
    <div className="homeLanding">
      <Toaster />

      <section
        className="homeLanding-hero"
        style={{ backgroundImage: `url(${BackgroundImage})` }}
      >
        <div className="homeLanding-heroOverlay" />
        <div className="homeLanding-heroContent">
          <div className="homeLanding-heroCopy">
            <h1 className="homeLanding-heroTitle">
              One Place To Schedule, Track And
              <br />
              Manage Maintenance & Repairs
            </h1>
            <p className="homeLanding-heroDescription">
              <span className="homeLanding-heroDescriptionLine">
                Properteasy is completely free to use. Submit a repair
                request, we dispatch a trusted local contractor and
              </span>
              <span className="homeLanding-heroDescriptionLine">
                you only pay for completed work.
              </span>
            </p>
            <div className="homeLanding-heroButtons">
              <button
                type="button"
                className="homeLanding-button homeLanding-buttonPrimary"
                onClick={handlePrimaryCta}
              >
                Sign up for FREE
              </button>
              <button
                type="button"
                className="homeLanding-button homeLanding-buttonSecondary"
                onClick={handleMaintenanceRequest}
              >
                Submit a maintenance request &rarr;
              </button>
            </div>
          </div>

          <p className="homeLanding-heroFootnote">
            Vetted local professionals &bull; Licensed &amp; insured &bull;
            Trusted by:
            <br />
            <span>
              Landlords, Homeowners, Property Managers, and Short Term Rental
              Hosts
            </span>
          </p>
        </div>
      </section>

      <div className="homeLanding-shell">
        <section className="homeLanding-section homeLanding-trustSection">
          <h2 className="homeLanding-sectionTitle">
            Why Property Owners Trust Properteasy
          </h2>
          <p className="homeLanding-sectionDescription">
            Any licensed professional can apply, but only the best are
            accepted. Every vendor in our network is fully vetted, so you can
            trust who enters your property and know the job will be done right.
          </p>
          <button
            type="button"
            className="homeLanding-linkButton"
            onClick={() => navigateAndScroll("/vendors")}
          >
            Apply to Become a Properteasy Vendor Today
          </button>

          <div className="homeLanding-trustGrid">
            {trustCards.map((card) => (
              <article className="homeLanding-trustCard" key={card.title}>
                <img src={card.icon} alt="" className="homeLanding-trustIcon" />
                <h3 className="homeLanding-trustTitle">{card.title}</h3>
                <p className="homeLanding-trustDescription">{card.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="homeLanding-section homeLanding-workflowSection">
          <h2 className="homeLanding-sectionTitle">
            How Properteasy Handles Property Maintenance
          </h2>
          <p className="homeLanding-sectionDescription">
            All requests, scheduling, communication, and billing managed in one
            place
          </p>

          <div className="homeLanding-workflowGrid">
            {workflowCards.map((card) => (
              <article className="homeLanding-workflowCard" key={card.number}>
                <div className="homeLanding-workflowCardHeader">
                  <span className="homeLanding-workflowNumber">
                    {card.number}
                  </span>
                  <h3 className="homeLanding-workflowTitle">{card.title}</h3>
                </div>
                <p className="homeLanding-workflowSubtitle">{card.subtitle}</p>
                <p className="homeLanding-workflowDescription">
                  {card.description}
                </p>
              </article>
            ))}
          </div>

          <button
            type="button"
            className="homeLanding-button homeLanding-actionButton homeLanding-outlineButton"
            onClick={() => navigateAndScroll("/landlords")}
          >
            See how it works for your property &rarr;
          </button>
        </section>

        <section
          className="homeLanding-serviceSection"
          style={{ backgroundImage: `url(${PatternImage})` }}
        >
          <h2 className="homeLanding-serviceTitle">What do you need done?</h2>
          <p className="homeLanding-serviceSubtitle">
            Professional property maintenance at your fingertips
          </p>
          <div className="homeLanding-serviceGrid">
            {serviceCards.map((card) => (
              <button
                type="button"
                className="homeLanding-serviceCard"
                key={card.title}
                onClick={handleMaintenanceRequest}
              >
                <img src={card.icon} alt="" className="homeLanding-serviceIcon" />
                <p className="homeLanding-serviceLabel">{card.title}</p>
              </button>
            ))}
          </div>
        </section>

        <section className="homeLanding-section homeLanding-ownerSection">
          <h2 className="homeLanding-sectionTitle">
            Free Maintenance Management For Every Type Of Property Owner
          </h2>

          <div className="homeLanding-ownerGrid">
            {ownerCards.map((card, index) => (
              <article
                className="homeLanding-ownerCard"
                key={card.title}
                onMouseEnter={() => setHoveredOwnerCardIndex(index)}
                onMouseLeave={() => setHoveredOwnerCardIndex(null)}
              >
                <div className="homeLanding-ownerHeader">
                  <div className="homeLanding-ownerIconWrap">
                    {typeof card.icon === "string" ? (
                      <img
                        src={card.icon}
                        alt=""
                        className="homeLanding-ownerIcon"
                      />
                    ) : (
                      <card.icon
                        className={`homeLanding-ownerIcon ${
                          card.isMuiIcon ? "homeLanding-ownerIcon-mui" : ""
                        }`}
                      />
                    )}
                  </div>
                  <h3 className="homeLanding-ownerTitle">{card.title}</h3>
                </div>

                <p className="homeLanding-ownerDescription">{card.description}</p>

                <button
                  type="button"
                  className="homeLanding-ownerButton"
                  onClick={() => navigateAndScroll(card.route)}
                >
                  {hoveredOwnerCardIndex === index
                    ? "See how it works"
                    : card.buttonText}
                </button>

                <hr className="homeLanding-ownerDivider" />

                <p className="homeLanding-ownerListHeading">You'll get:</p>
                <ul className="homeLanding-ownerList">
                  {card.items.map((item) => (
                    <li className="homeLanding-ownerListItem" key={item}>
                      <img
                        src={CheckCircleIcon}
                        alt=""
                        className="homeLanding-checkIcon"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <VendorApplyCTA
          descriptionClassName="homeLanding-vendorDescriptionHome"
          onButtonClick={() => navigateAndScroll("/vendors")}
        />

        {blogs.length > 0 && (
          <section className="homeLanding-insightsSection">
            <div className="homeLanding-insightsInner">
              <div className="homeLanding-insightsHeader">
                <div>
                  <h2 className="homeLanding-sectionTitle">
                    Property Maintenance Insights
                  </h2>
                  <p className="homeLanding-sectionDescription">
                    Practical articles designed to help owners and managers make
                    informed decisions.
                  </p>
                </div>
                <button
                  type="button"
                  className="homeLanding-button homeLanding-actionButton homeLanding-outlineButton"
                  onClick={() => navigateAndScroll("/blog")}
                >
                  See all articles
                </button>
              </div>

              <div className="homeLanding-insightsGrid">
                {blogs.map((post) => {
                  const publishDate = post.publishedAt
                    ? new Date(post.publishedAt)
                    : null;
                  const formattedDate =
                    publishDate && !isNaN(publishDate)
                      ? publishDate.toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "Recent";

                  return (
                    <article
                      key={post.id}
                      className="homeLanding-insightCard"
                      onClick={() => navigateAndScroll(`/blog/${post.slug}`)}
                      style={{ cursor: "pointer" }}
                    >
                      {post.heroImage ? (
                        <img
                          src={buildImageUrl(post.heroImage)}
                          alt={post.title}
                          className="homeLanding-insightImage"
                        />
                      ) : (
                        <div className="homeLanding-insightImagePlaceholder">
                          <img
                            src={LogoImage}
                            alt="Properteasy logo"
                            className="homeLanding-insightImagePlaceholderLogo"
                          />
                        </div>
                      )}
                      <p className="homeLanding-insightMeta">
                        Properteasy Team • {formattedDate}
                      </p>
                      <h3 className="homeLanding-insightTitle">{post.title}</h3>
                      <p className="homeLanding-insightDescription">
                        {post.excerpt}
                      </p>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <section className="homeLanding-section homeLanding-reviewsSection">
          <h2 className="homeLanding-centerTitle">See what our clients say</h2>
          <p className="homeLanding-centerDescription">
            Discover why users love our platform and how it's making a positive
            impact on their work and businesses.
          </p>

          <div className="homeLanding-reviewsGrid">
            {testimonials.map((testimonial) => (
              <article className="homeLanding-reviewCard" key={testimonial.title}>
                <div className="homeLanding-reviewStars">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <img
                      src={StarReviewsIcon}
                      alt=""
                      key={`${testimonial.title}-star-${index}`}
                    />
                  ))}
                </div>
                <h3 className="homeLanding-reviewTitle">{testimonial.title}</h3>
                <p className="homeLanding-reviewDescription">
                  {testimonial.description}
                </p>
              </article>
            ))}
          </div>

          {/* <div className="homeLanding-reviewDots">
            <span className="homeLanding-reviewDot homeLanding-reviewDotActive" />
            <span className="homeLanding-reviewDot" />
            <span className="homeLanding-reviewDot" />
          </div> */}
        </section>

        <section className="homeLanding-section homeLanding-faqSection">
          <div className="homeLanding-faqLeftColumn">
            <h2 className="homeLanding-sectionTitle">Frequently Asked Questions</h2>
            <p className="homeLanding-sectionDescription">
              Got questions? We&apos;ve got answers. Find everything you need to
              know about using Properteasy.
            </p>
            {/* <button
              type="button"
              className="homeLanding-linkButton"
              onClick={() => navigateAndScroll("/legal")}
            >
              View all
            </button> */}
          </div>

          <div className="homeLanding-faqRightColumn">
            {faqs.map((faq, index) => (
              <div className="homeLanding-faqItem" key={faq.question}>
                <button
                  type="button"
                  className={`homeLanding-faqQuestionButton ${
                    openFaqIndex === index
                      ? "homeLanding-faqQuestionButtonActive"
                      : ""
                  }`}
                  aria-expanded={openFaqIndex === index}
                  onClick={() =>
                    setOpenFaqIndex(openFaqIndex === index ? null : index)
                  }
                >
                  <span>{faq.question}</span>
                  <img
                    src={openFaqIndex === index ? MinusIcon : PlusIcon}
                    alt=""
                    className="homeLanding-faqToggleIcon"
                  />
                </button>
                {openFaqIndex === index && (
                  <p className="homeLanding-faqAnswer">
                    {normalizedFaqAnswer(faq.answer)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

      </div>

      <section
        className="homeLanding-newsletterSection"
        style={{ backgroundImage: `url(${PatternImage})` }}
      >
        <div className="homeLanding-newsletterInner">
          <h2 className="homeLanding-newsletterTitle">
            Stay Updated with Properteasy
          </h2>
          <p className="homeLanding-newsletterDescription">
            Get the latest tips on property maintenance, industry insights, and
            platform updates delivered to your inbox.
          </p>

          <form
            className="homeLanding-newsletterForm"
            onSubmit={handleNewsletterSubmit}
          >
            <input
              type="email"
              value={newsletterEmail}
              onChange={(event) => setNewsletterEmail(event.target.value)}
              placeholder="Enter your email address"
              className="homeLanding-newsletterInput"
              disabled={newsletterLoading}
              aria-label="Email address"
            />
            <button
              type="submit"
              className="homeLanding-button homeLanding-newsletterButton"
              disabled={newsletterLoading}
            >
              {newsletterLoading
                ? "Subscribing..."
                : "Subscribe to our Newsletter"}
            </button>
          </form>

          <p className="homeLanding-newsletterFinePrint">
            No spam, unsubscribe at any time. We respect your privacy.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
